/**
 * Grafico del rendimento (F-182): wrapper su `lightweight-charts` v5, come
 * `PriceChart` (ADR-0009) — la libreria è imperativa e resta confinata qui.
 *
 * Ciò che deve saltare all'occhio è **da che parte dello zero** sta il
 * portafoglio, non quanto ha fatto oggi. Per questo:
 *
 * - la curva scelta è una `BaselineSeries` sullo zero: verde sopra, rossa sotto,
 *   con il riempimento fino alla linea del pareggio;
 * - la linea dello zero è spessa, scura e porta l'etichetta «pareggio», e lo
 *   zero resta **sempre** nella scala: una curva tutta in guadagno disegnata
 *   senza zero sembrerebbe oscillare fra profitto e perdita;
 * - le candele hanno dietro la stessa area tenue, così anche lì si legge lo
 *   stato e non solo la direzione;
 * - acquisti e vendite sono segni blu e arancio, non verdi e rossi: quei due
 *   colori qui significano già guadagno e perdita.
 *
 * Nessun colore scritto: tutti dai token CSS a runtime (ADR-0005).
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BaselineSeries,
  CandlestickSeries,
  createChart,
  createSeriesMarkers,
  LineSeries,
  LineStyle,
  type AutoscaleInfo,
  type IChartApi,
  type ISeriesApi,
  type SeriesMarker,
  type SeriesType,
  type Time,
} from 'lightweight-charts';
import {
  METRIC_LABELS,
  findGaps,
  operationKind,
  type PerformanceBucket,
  type PerformanceMetric,
} from './performance-series';
import { overlayToken, withAlpha } from './performance-colors';

export type PerformanceChartMode = 'line' | 'candles';

/** Una curva di contorno, sottile, per il confronto. */
export type PerformanceOverlay = { metric: PerformanceMetric; buckets: PerformanceBucket[] };

type Props = {
  buckets: PerformanceBucket[];
  metric: PerformanceMetric;
  mode: PerformanceChartMode;
  overlays: PerformanceOverlay[];
  currency: string;
  selected: string | null;
  onSelect: (time: string) => void;
  /** Il contenuto della nuvoletta per il secchio sotto il cursore. */
  renderTooltip: (bucket: PerformanceBucket) => ReactNode;
  height?: number;
};

function cssToken(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Etichette dell'asse: senza centesimi sopra i cento euro, dove sono rumore;
 * con i centesimi sotto, dove un portafoglio piccolo avrebbe tacche tutte
 * uguali («1 €, 1 €, 1 €»).
 */
function axisFormatter(currency: string): (price: number) => string {
  const whole = new Intl.NumberFormat('it-IT', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const cents = new Intl.NumberFormat('it-IT', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (price) => (Math.abs(price) >= 100 ? whole : cents).format(price);
}

/** Lo zero sempre dentro la scala. */
function withZero(original: () => AutoscaleInfo | null): AutoscaleInfo | null {
  const info = original();
  if (!info?.priceRange) return info;
  return {
    ...info,
    priceRange: {
      minValue: Math.min(info.priceRange.minValue, 0),
      maxValue: Math.max(info.priceRange.maxValue, 0),
    },
  };
}

type Hover = { bucket: PerformanceBucket; x: number };

export function PerformanceChart({
  buckets,
  metric,
  mode,
  overlays,
  currency,
  selected,
  onSelect,
  renderTooltip,
  height = 400,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const [hover, setHover] = useState<Hover | null>(null);
  const [selectedX, setSelectedX] = useState<number | null>(null);
  const [width, setWidth] = useState(0);

  const byTime = useMemo(() => new Map(buckets.map((bucket) => [bucket.time, bucket])), [buckets]);
  const hasValues = buckets.some((bucket) => bucket.close !== null);

  // `onSelect` cambia identità a ogni render del genitore: tenerlo in un ref
  // evita di ricostruire il grafico (e perdere zoom e scorrimento) per questo.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !hasValues) return;

    const text = cssToken('--color-text');
    const muted = cssToken('--color-text-muted');
    const subtle = cssToken('--color-text-subtle');
    const border = cssToken('--color-border');
    const positive = cssToken('--color-positive');
    const negative = cssToken('--color-negative');

    const chart = createChart(container, {
      height,
      autoSize: true,
      layout: { background: { color: 'transparent' }, textColor: muted, attributionLogo: false },
      localization: {
        locale: 'it-IT',
        priceFormatter: axisFormatter(currency),
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: border, style: LineStyle.Dotted },
      },
      rightPriceScale: { borderColor: border, scaleMargins: { top: 0.12, bottom: 0.12 } },
      timeScale: { borderColor: border, fixLeftEdge: true, fixRightEdge: true },
      crosshair: { horzLine: { visible: false, labelVisible: false } },
      handleScroll: { vertTouchDrag: false },
    });
    chartRef.current = chart;

    const area = (lineWidth: 1 | 2 | 3, fill: number, lineVisible: boolean) =>
      chart.addSeries(BaselineSeries, {
        baseValue: { type: 'price', price: 0 },
        topLineColor: lineVisible ? positive : 'transparent',
        bottomLineColor: lineVisible ? negative : 'transparent',
        topFillColor1: withAlpha(positive, fill),
        topFillColor2: withAlpha(positive, fill / 6),
        bottomFillColor1: withAlpha(negative, fill / 6),
        bottomFillColor2: withAlpha(negative, fill),
        lineWidth,
        priceLineVisible: false,
        lastValueVisible: lineVisible,
        crosshairMarkerVisible: lineVisible,
        autoscaleInfoProvider: withZero,
      });

    // Ogni giorno è nella scala dei tempi, anche senza valore: è li' che stanno
    // le operazioni di un giorno senza fotografia, e il cursore deve trovarle.
    const lineData = buckets.map((bucket) =>
      bucket.close === null ? { time: bucket.time as Time } : { time: bucket.time as Time, value: bucket.close },
    );

    const main = mode === 'line' ? area(3, 0.28, true) : area(1, 0.1, false);
    main.setData(lineData);

    for (const overlay of mode === 'line' ? overlays : []) {
      const series = chart.addSeries(LineSeries, {
        color: cssToken(overlayToken(overlay.metric)),
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        autoscaleInfoProvider: withZero,
        title: METRIC_LABELS[overlay.metric],
      });
      series.setData(
        overlay.buckets
          .filter((bucket) => bucket.close !== null)
          .map((bucket) => ({ time: bucket.time as Time, value: bucket.close! })),
      );
    }

    // Buchi: tratteggio grigio solo visivo fra i due giorni con un valore.
    // Mai riempito: fra due fotografie il valore di mercato non e' noto.
    if (mode === 'line') {
      for (const gap of findGaps(buckets)) {
        const dashed = chart.addSeries(LineSeries, {
          color: subtle,
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        dashed.setData([
          { time: gap.from.time as Time, value: gap.from.value },
          { time: gap.to.time as Time, value: gap.to.value },
        ]);
      }
    }

    let markerTarget: ISeriesApi<SeriesType> = main;
    if (mode === 'candles') {
      const candles = chart.addSeries(CandlestickSeries, {
        upColor: positive,
        downColor: negative,
        borderUpColor: positive,
        borderDownColor: negative,
        wickUpColor: positive,
        wickDownColor: negative,
        priceLineVisible: false,
        autoscaleInfoProvider: withZero,
      });
      candles.setData(
        buckets.map((bucket) =>
          bucket.close === null
            ? { time: bucket.time as Time }
            : {
                time: bucket.time as Time,
                open: bucket.open!,
                high: bucket.high!,
                low: bucket.low!,
                close: bucket.close,
              },
        ),
      );
      markerTarget = candles;
    }

    // La linea del pareggio: la cosa piu' importante del grafico.
    markerTarget.createPriceLine({
      price: 0,
      color: text,
      lineWidth: 2,
      lineStyle: LineStyle.Solid,
      axisLabelVisible: true,
      title: 'pareggio',
    });

    const buy = cssToken('--color-accent');
    const sell = cssToken('--color-cat-4');
    const income = cssToken('--color-cat-3');
    // La libreria scala i segni con la larghezza delle barre: con poche
    // candele larghe (settimane, 7 giorni) coprirebbero il grafico.
    const size = buckets.length < 20 ? 0.5 : 1;
    const markers: SeriesMarker<Time>[] = [];
    for (const bucket of buckets) {
      const time = bucket.time as Time;
      const kinds = new Set(bucket.operations.map((operation) => operationKind(operation.txType)));
      if (kinds.has('buy')) markers.push({ time, position: 'belowBar', shape: 'arrowUp', color: buy, size });
      if (kinds.has('sell')) markers.push({ time, position: 'aboveBar', shape: 'arrowDown', color: sell, size });
      if (kinds.has('income')) markers.push({ time, position: 'belowBar', shape: 'circle', color: income, size });
    }
    createSeriesMarkers(markerTarget, markers);

    chart.timeScale().fitContent();

    chart.subscribeCrosshairMove((param) => {
      const bucket = param.time ? byTime.get(param.time as string) : undefined;
      setHover(bucket && param.point ? { bucket, x: param.point.x } : null);
    });
    chart.subscribeClick((param) => {
      if (param.time) onSelectRef.current(param.time as string);
    });

    return () => {
      chartRef.current = null;
      chart.remove();
    };
  }, [buckets, byTime, hasValues, metric, mode, overlays, currency, height]);

  // La banda del giorno fissato segue zoom e scorrimento.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const update = () => {
      setSelectedX(selected ? chart.timeScale().timeToCoordinate(selected as Time) : null);
      setWidth(containerRef.current?.clientWidth ?? 0);
    };
    update();
    chart.timeScale().subscribeVisibleLogicalRangeChange(update);
    return () => chart.timeScale().unsubscribeVisibleLogicalRangeChange(update);
  }, [selected, buckets, mode, overlays]);

  if (!hasValues) {
    return (
      <div className="perf-chart perf-chart--empty" style={{ minHeight: height }}>
        <p>Nessun valore in questo periodo per la curva «{METRIC_LABELS[metric].toLowerCase()}».</p>
      </div>
    );
  }

  const tooltipLeft = hover ? (hover.x > width - 300 ? Math.max(8, hover.x - 296) : hover.x + 16) : 0;

  return (
    <div className="perf-chart" onMouseLeave={() => setHover(null)}>
      <div ref={containerRef} className="perf-chart__canvas" style={{ height }} data-testid="performance-chart" />
      {selectedX !== null && <div className="perf-chart__selected" style={{ left: selectedX }} aria-hidden="true" />}
      {hover && (
        <div className="perf-chart__tooltip" role="status" style={{ left: tooltipLeft }}>
          {renderTooltip(hover.bucket)}
        </div>
      )}
    </div>
  );
}
